/**
 * IAM managed-policy hooks — list/detail/create/delete. Every hook is scoped by
 * `serviceId` (the connection + credential is resolved server-side from it), so
 * all query keys carry it. The policy `arn` is opaque and may contain reserved
 * URL characters, so it is always sent encoded as a query param.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { serviceApiV3, unwrap, storageBase, s3Keys } from './_shared';

/** One managed policy as listed (`GET /storage/policies`). */
export interface Policy {
  policy_name: string;
  arn: string;
  policy_id?: string;
  attachment_count?: number;
  is_attachable?: boolean;
  create_date?: string | null;
  update_date?: string | null;
}

/** Principals a policy is attached to. */
export interface PolicyEntities {
  users: string[];
  groups: string[];
  roles: string[];
}

/** Full policy with its document (`GET /storage/policies/detail`). */
export interface PolicyDetail {
  policy_name: string;
  arn: string;
  /** Pretty-printed JSON string of the policy document. */
  document: string;
  policy_id?: string;
  description?: string;
  attachment_count?: number;
  is_attachable?: boolean;
  /** True for AWS-managed built-ins (arn:aws:iam::aws:policy/…). */
  managed?: boolean;
  default_version?: string;
  create_date?: string | null;
  update_date?: string | null;
  /** Users/groups/roles this policy is attached to. */
  entities?: PolicyEntities;
}

/** Freshly-created policy — the create response echoes name + arn. */
export interface NewPolicy {
  arn: string;
  policy_name: string;
}

export interface CreatePolicyInput {
  policy_name: string;
  /** JSON policy document as a string (validated client-side before send). */
  document: string;
}

/** List the service's managed policies. */
export function usePolicies(serviceId: string | undefined) {
  return useQuery<Policy[]>({
    queryKey: s3Keys.policies(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 30_000,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${storageBase(serviceId!)}/policies`);
      const data = unwrap<{ policies?: Policy[] }>(res);
      return data?.policies ?? [];
    },
  });
}

/** Fetch one policy (name + arn + document) by its arn. */
export function usePolicy(serviceId: string | undefined, arn: string | undefined) {
  return useQuery<PolicyDetail>({
    queryKey: s3Keys.policy(serviceId ?? '', arn ?? ''),
    enabled: !!serviceId && !!arn,
    queryFn: async () =>
      unwrap<PolicyDetail>(
        await serviceApiV3.get(
          `${storageBase(serviceId!)}/policies/detail?arn=${encodeURIComponent(arn!)}`,
        ),
      ),
  });
}

/** Create a managed policy from a name + JSON document. */
export function useCreatePolicy(serviceId: string) {
  const qc = useQueryClient();
  return useMutation<NewPolicy, Error, CreatePolicyInput>({
    mutationFn: async (input) => {
      const res = await serviceApiV3.post(`${storageBase(serviceId)}/policies`, input);
      return unwrap<NewPolicy>(res);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: s3Keys.policies(serviceId) });
    },
  });
}

export interface UpdatePolicyInput {
  arn: string;
  /** New JSON policy document (validated client-side before send). */
  document: string;
}

/** Edit a customer-managed policy — writes a new default version. AWS-managed
 *  policies are read-only server-side (403). */
export function useUpdatePolicy(serviceId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, UpdatePolicyInput>({
    mutationFn: async ({ arn, document }) => {
      await serviceApiV3.put(
        `${storageBase(serviceId)}/policies?arn=${encodeURIComponent(arn)}`,
        { document },
      );
    },
    onSuccess: (_data, { arn }) => {
      qc.invalidateQueries({ queryKey: s3Keys.policy(serviceId, arn) });
      qc.invalidateQueries({ queryKey: s3Keys.policies(serviceId) });
    },
  });
}

/** Permanently delete a policy by arn — any principal relying on it loses those grants. */
export function useDeletePolicy(serviceId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (arn) => {
      await serviceApiV3.delete(
        `${storageBase(serviceId)}/policies?arn=${encodeURIComponent(arn)}`,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: s3Keys.policies(serviceId) });
    },
  });
}
